import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon, MailCheckIcon } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { z } from 'zod';
import type { CompanyCreateMemberDTO } from '@shared/schemas/organization';
import { companyCreateMemberSchema } from '@shared/schemas/organization';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import type { CompanyMembersScope } from './company-api';
import { getCompanyErrorMessage } from './company-errors';
import { CompanyMemberRoleField } from './CompanyMemberRoleField';
import { useCreateCompanyMember } from './useCreateCompanyMember';

interface CompanyMemberFormProps {
  listPath: string;
  scope: CompanyMembersScope;
}

type CompanyMemberFormInput = z.input<typeof companyCreateMemberSchema>;

export function CompanyMemberForm({ listPath, scope }: CompanyMemberFormProps) {
  const navigate = useNavigate();
  const createMember = useCreateCompanyMember(listPath, scope);
  const form = useForm<CompanyMemberFormInput, undefined, CompanyCreateMemberDTO>({
    defaultValues: {
      accountType: 'subscriber',
      email: '',
      name: '',
      role: 'member',
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: zodResolver(companyCreateMemberSchema),
  });
  const selectedRole = useWatch({ control: form.control, name: 'role' });
  async function handleSubmit(values: CompanyCreateMemberDTO) {
    form.clearErrors('root');
    try {
      await createMember.submit(values);
    } catch (error) {
      const message = getCompanyErrorMessage(error);
      form.setError('root', { message, type: 'server' });
      toast.error(message);
    }
  }

  return (
    <form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
      <FieldSet disabled={form.formState.isSubmitting || createMember.isPending}>
        <FieldLegend className="sr-only">Informations du nouveau membre</FieldLegend>
        <FieldGroup>
          <div className="grid gap-5 lg:grid-cols-2">
            <Field data-invalid={Boolean(form.formState.errors.name)}>
              <FieldLabel htmlFor="company-member-name">Nom complet</FieldLabel>
              <Input
                id="company-member-name"
                aria-describedby={form.formState.errors.name ? 'company-member-name-error' : undefined}
                aria-invalid={Boolean(form.formState.errors.name)}
                autoComplete="name"
                data-testid="company-member-name"
                {...form.register('name')}
              />
              <FieldError id="company-member-name-error" errors={[form.formState.errors.name]} />
            </Field>
            <Field data-invalid={Boolean(form.formState.errors.email)}>
              <FieldLabel htmlFor="company-member-email">Adresse e-mail</FieldLabel>
              <Input
                id="company-member-email"
                aria-describedby={form.formState.errors.email ? 'company-member-email-error' : undefined}
                aria-invalid={Boolean(form.formState.errors.email)}
                autoComplete="email"
                data-testid="company-member-email"
                type="email"
                {...form.register('email')}
              />
              <FieldError id="company-member-email-error" errors={[form.formState.errors.email]} />
            </Field>
          </div>
          <Alert>
            <MailCheckIcon />
            <AlertTitle>Activation automatique</AlertTitle>
            <AlertDescription>
              Un e-mail permettra au membre de définir son mot de passe et d’activer son accès.
            </AlertDescription>
          </Alert>
          <CompanyMemberRoleField
            error={form.formState.errors.role}
            onChange={(role) => form.setValue('role', role, {
              shouldDirty: true,
              shouldValidate: true,
            })}
            value={selectedRole ?? 'member'}
          />
          <FieldError errors={[form.formState.errors.root]} />
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => navigate(listPath)}>
              Annuler
            </Button>
            <Button
              aria-busy={form.formState.isSubmitting}
              data-testid="company-member-submit"
              disabled={form.formState.isSubmitting}
              type="submit"
            >
              {form.formState.isSubmitting
                ? <Loader2Icon className="animate-spin" data-icon="inline-start" />
                : null}
              Créer le membre
            </Button>
          </div>
        </FieldGroup>
      </FieldSet>
    </form>
  );
}
